import React, { useEffect, useMemo, useRef, useState } from 'react';
import './SearchableSelect.css';

const normalizeText = (value) => (
  (value || '')
    .toString()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
);

const SearchableSelect = ({
  options = [],
  value = '',
  onChange,
  placeholder = 'Buscar...',
  emptyMessage = 'No se encontraron resultados.',
  noOptionsMessage = 'No hay opciones disponibles.',
  disabled = false
}) => {
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  const selectedOption = useMemo(
    () => options.find((option) => option.value === value) || null,
    [options, value]
  );

  const filteredOptions = useMemo(() => {
    const term = normalizeText(query.trim());
    if (!term) {
      return options;
    }

    return options.filter((option) => normalizeText([
      option.label,
      option.meta,
      option.searchText
    ].filter(Boolean).join(' ')).includes(term));
  }, [options, query]);

  useEffect(() => {
    if (!isOpen) {
      setQuery(selectedOption?.label || '');
    }
  }, [isOpen, selectedOption]);

  useEffect(() => {
    const handlePointerDown = (event) => {
      if (!containerRef.current?.contains(event.target)) {
        setIsOpen(false);
        setHighlightedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, []);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setHighlightedIndex(filteredOptions.length > 0 ? 0 : -1);
  }, [filteredOptions.length, isOpen]);

  const handleSelect = (option) => {
    onChange?.(option.value);
    setQuery(option.label);
    setIsOpen(false);
    setHighlightedIndex(-1);
  };

  const handleInputChange = (event) => {
    const nextValue = event.target.value;
    setQuery(nextValue);
    setIsOpen(true);
    setHighlightedIndex(0);

    if (selectedOption && nextValue !== selectedOption.label) {
      onChange?.('');
    }
  };

  const handleInputFocus = () => {
    if (disabled) {
      return;
    }

    setIsOpen(true);
    inputRef.current?.select();
  };

  const handleKeyDown = (event) => {
    if (disabled) {
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (!isOpen) {
        setIsOpen(true);
        return;
      }

      setHighlightedIndex((current) => (
        filteredOptions.length === 0 ? -1 : Math.min(current + 1, filteredOptions.length - 1)
      ));
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (!isOpen) {
        setIsOpen(true);
        return;
      }

      setHighlightedIndex((current) => Math.max(current - 1, 0));
      return;
    }

    if (event.key === 'Enter' && isOpen && highlightedIndex >= 0 && filteredOptions[highlightedIndex]) {
      event.preventDefault();
      handleSelect(filteredOptions[highlightedIndex]);
      return;
    }

    if (event.key === 'Escape') {
      setIsOpen(false);
      setHighlightedIndex(-1);
      setQuery(selectedOption?.label || '');
    }
  };

  const statusMessage = options.length === 0 ? noOptionsMessage : emptyMessage;

  return (
    <div className={`searchable-select ${disabled ? 'is-disabled' : ''}`} ref={containerRef}>
      <div className={`searchable-select-control ${isOpen ? 'is-open' : ''}`}>
        <i className="fas fa-search searchable-select-icon" aria-hidden="true" />
        <input
          ref={inputRef}
          type="text"
          className="searchable-select-input"
          value={query}
          placeholder={placeholder}
          onFocus={handleInputFocus}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          disabled={disabled}
        />
        <i className={`fas fa-chevron-${isOpen ? 'up' : 'down'} searchable-select-chevron`} aria-hidden="true" />
      </div>

      {isOpen && (
        <div className="searchable-select-menu">
          {filteredOptions.length === 0 ? (
            <div className="searchable-select-empty">{statusMessage}</div>
          ) : (
            filteredOptions.map((option, index) => (
              <button
                key={option.value}
                type="button"
                className={`searchable-select-option ${index === highlightedIndex ? 'is-highlighted' : ''} ${option.value === value ? 'is-selected' : ''}`}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => handleSelect(option)}
              >
                <span className="searchable-select-option-label">{option.label}</span>
                {option.meta && (
                  <span className="searchable-select-option-meta">{option.meta}</span>
                )}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default SearchableSelect;
